/**
 * tax/LoanFpView.tsx — 팀장님 대출 접수 링크(/loan/<token>?w=fp)의 「보낸 대출 진행」 (2026-10-27 지도사
 *   「이 링크에서도 신청한 대출들에 대한 진행 상황 확인 가능하도록 · 여기 바로 들어가실 분들도 계실 거라」)
 *  이 링크는 대표님께 그대로 가기도 해서 링크만으로는 안 보인다
 *  2026-10-28 지도사 「팀장님이 쓰시는 건 이런 허들 없애」 — 팀장님 몫 열쇠(tax/loanKey)가 있으면 인증번호 없이 바로
 *   (팀장님께 보낸 주소 ?k= · 팀장님 포털을 연 기기) · 인증번호는 열쇠 없는 예전 주소만 쓰는 대안
 *   예전 주소도 한 번에(2026-10-28 지도사 「안전하게 + 원터치」): 「진행 보기」 = 문자 바로 보냄 · 안드로이드는 문자의 번호를 자동으로 넣고(Web OTP)
 *   아이폰은 키보드 위 번호를 한 번 누르면 들어감 · 6자리가 차면 바로 확인 — 단추를 더 누르지 않는다
 *  글자 · 단추는 접수 화면과 같은 크기(16~18 · 높이 48 이상) · 서버 routes/fp-loan.js view-code · view-verify · loans
 */
import React from 'react';
import { X } from './taxUi';
import { dropLoanKey, readLoanKey, takeKeyFromUrl } from './loanKey';

/** 선정 안 됨 대책 — ① 왜 안 됐는지 ② 보완할 점 ③ 다음 신청(2026-10-27 지도사 「고객이 납득가능하도록」) */
interface RoundResult { reason: string; improve: string; next: string; open: boolean }
interface Branch { label: string; fund: string; state: 'live' | 'done' | 'closed' | 'stopped'; now: string; done: number; total: number; steps: { name: string; status: string }[]; terms: { amount: number | null; rate: string; when: string; state: string; lender?: string } | null; result?: RoundResult | null }
interface Loan { id: string; company: string; created: string; status: string; next_check: string | null; news: { day: string; text: string } | null; common: Branch | null; branches: Branch[] }

const KEY = (t: string) => `loanView:${t}`;
const md = (d: string | null) => (d && d.length >= 10 ? `${+d.slice(5, 7)}/${+d.slice(8, 10)}` : '');
const won = (man: number | null) => { if (man == null) return ''; const e = Math.floor(man / 10000), r = man % 10000; return e ? `${e}억${r ? ` ${r.toLocaleString('ko-KR')}만` : ''}원` : `${r.toLocaleString('ko-KR')}만원`; };

async function call<T>(token: string, method: 'GET' | 'POST', path: string, body?: unknown, view?: string, key?: string): Promise<{ ok: boolean; status: number; data?: T; error?: string }> {
  try {
    const r = await fetch(`/api/fp-loan/c/${encodeURIComponent(token)}${path}`, { method, headers: { 'Content-Type': 'application/json', ...(view ? { 'X-View-Token': view } : {}), ...(key ? { 'X-View-Key': key } : {}) }, body: body ? JSON.stringify(body) : undefined });
    const j = await r.json().catch(() => ({})) as { success?: boolean; data?: T; error?: string };
    return { ok: r.ok && !!j.success, status: r.status, data: j.data, error: j.error };
  } catch (e) { return { ok: false, status: 0, error: '연결이 잠시 안 돼요' }; }
}

export default function LoanFpView({ token, ink, role = '팀장' }: { token: string; ink: string; role?: string }) {
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState<string>(() => { try { return localStorage.getItem(KEY(token)) || ''; } catch (e) { return ''; } });
  const [key, setKey] = React.useState<string>(() => takeKeyFromUrl(token) || readLoanKey(token));   // 팀장님 몫 열쇠 — 있으면 인증번호 없이(2026-10-28)
  const ready = !!(key || view);
  const [loans, setLoans] = React.useState<Loan[] | null>(null);
  const [sentTo, setSentTo] = React.useState('');
  const [code, setCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const load = React.useCallback(async (v: string, k: string) => {
    const r = await call<{ loans: Loan[] }>(token, 'GET', '/loans', undefined, v, k);
    if (r.ok && r.data) { setLoans(r.data.loans); return; }
    // 열쇠가 안 맞음(링크를 새로 만듦 · 인증 30일 지남) — 둔 것을 지우고 인증번호 대안으로
    if (r.status === 401) { try { localStorage.removeItem(KEY(token)); } catch (e) { /* 그대로 */ } dropLoanKey(token); setView(''); setKey(''); setLoans(null); return; }
    setMsg(r.error || '불러오지 못했어요');
  }, [token]);
  React.useEffect(() => { if (open && ready && !loans) void load(view, key); }, [open, ready, view, key, loans, load]);
  // 열쇠 없는 예전 주소 — 「진행 보기」를 누르면 바로 문자(단추 하나 덜 · 2026-10-28)
  const asked = React.useRef(false);
  React.useEffect(() => { if (open && !ready && !sentTo && !asked.current) { asked.current = true; void askCode(); } }, [open, ready, sentTo]);   // eslint-disable-line react-hooks/exhaustive-deps
  // 안드로이드 크롬 — 문자의 끝줄 「@도메인 #번호」를 읽어 자동으로 넣고 확인(Web OTP) · 못 하면 그냥 손으로
  React.useEffect(() => {
    if (!sentTo || ready) return undefined;
    const nav = navigator as Navigator & { credentials?: { get: (o: unknown) => Promise<{ code?: string } | null> } };
    if (!('OTPCredential' in window) || !nav.credentials) return undefined;
    const ac = new AbortController();
    nav.credentials.get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((o) => { const c = String((o && o.code) || '').replace(/\D/g, '').slice(0, 6); if (c.length === 6) { setCode(c); void verify(c); } })
      .catch(() => { /* 손으로 넣으면 됨 */ });
    return () => ac.abort();
  }, [sentTo, ready]);   // eslint-disable-line react-hooks/exhaustive-deps

  const askCode = async () => {
    setBusy(true); setMsg('');
    const r = await call<{ to: string; minutes: number }>(token, 'POST', '/view-code');
    setBusy(false);
    if (r.ok && r.data) { setSentTo(r.data.to); setCode(''); } else setMsg(r.error || '문자를 보내지 못했어요');
  };
  const verify = async (c: string = code) => {
    if (busy) return;
    setBusy(true); setMsg('');
    const r = await call<{ viewToken: string }>(token, 'POST', '/view-verify', { code: c });
    setBusy(false);
    if (r.ok && r.data) { try { localStorage.setItem(KEY(token), r.data.viewToken); } catch (e) { /* 이번만 */ } setView(r.data.viewToken); setSentTo(''); }   // 목록은 열쇠가 생기면 위 효과가 한 번 불러 온다
    else setMsg(r.error || '확인하지 못했어요');
  };

  const btn = (primary: boolean): React.CSSProperties => ({ minHeight: 48, padding: '0 18px', borderRadius: 12, border: primary ? 'none' : `1.5px solid ${ink}`, background: primary ? ink : '#fff', color: primary ? '#fff' : ink, fontFamily: 'inherit', fontSize: 16, fontWeight: 700, cursor: busy ? 'progress' : 'pointer', whiteSpace: 'nowrap' });
  const branchLine = (b: Branch) => {
    const head = [b.label, b.fund].filter(Boolean).join(' · ');
    const state = b.state === 'done' ? '대출 실행까지 끝났어요' : b.state === 'closed' ? '이번에는 선정되지 않았어요' : b.state === 'stopped' ? '진행을 멈췄어요' : `${b.total}단계 중 ${Math.min(b.done + 1, b.total)}번째 · ${b.now}`;
    const t = b.terms;
    return (
      <div key={head} style={{ padding: '10px 12px', borderRadius: 10, background: X.soft }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: X.text }}>{head}</div>
        <div style={{ fontSize: 15, color: b.state === 'done' ? X.green : b.state === 'live' ? X.label : X.sub, marginTop: 2 }}>{state}</div>
        {t && (t.amount || t.rate || t.when || t.lender) && <div style={{ fontSize: 15, color: X.text, marginTop: 2 }}>대출 결과 · {[t.lender || '', won(t.amount), t.rate ? `연 ${t.rate}%` : '', t.when ? `${md(t.when)} 실행${t.state === 'done' ? '' : ' 예정'}` : ''].filter(Boolean).join(' · ')}</div>}
        {b.result && <dl style={{ margin: '8px 0 0', display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', gap: '4px 10px', fontSize: 15, lineHeight: 1.5 }}>
          {([['이유', b.result.reason], ['보완할 점', b.result.improve], ['다음 신청', b.result.next]] as const).map(([k, v]) => (
            <React.Fragment key={k}><dt style={{ color: X.label, whiteSpace: 'nowrap' }}>{k}</dt><dd style={{ margin: 0, color: X.text, wordBreak: 'keep-all', whiteSpace: 'pre-wrap' }}>{v}</dd></React.Fragment>
          ))}
        </dl>}
      </div>
    );
  };

  // 2026-10-28 지도사 「진행현황 보기는 우측 상단이든 어디든 숨겨도 돼」 — 접수 칸 사이 큰 상자 대신 머리 오른쪽 작은 단추 · 누르면 화면 전체 판
  return (
    <>
      <button type="button" aria-expanded={open} onClick={() => { setOpen(true); setMsg(''); }}
        style={{ flex: 'none', minHeight: 44, padding: '0 4px', border: 'none', background: 'none', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: ink, cursor: 'pointer', whiteSpace: 'nowrap' }}>보낸 대출 진행 ›</button>
      {open && (
    <div role="dialog" aria-label="보낸 대출 진행" style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#fff', overflowY: 'auto', overscrollBehavior: 'contain' }}>
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '12px 16px 40px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 56, position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: X.text }}>보낸 대출 진행</div>
          {!ready && <div style={{ fontSize: 15, color: X.label, marginTop: 2 }}>처음 한 번만 문자로 확인해요</div>}
        </div>
        <button type="button" onClick={() => { setOpen(false); setMsg(''); }} style={{ ...btn(false), flex: '0 0 auto' }}>닫기</button>
      </div>

      {open && !ready && (
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 10, wordBreak: 'keep-all' }}>   {/* 칸이 글 · 입력칸 폭만큼 늘어나 오른쪽이 잘리던 것(2026-10-28) */}
          {!sentTo
            ? (busy || !msg
                ? <div style={{ fontSize: 15, color: X.label }}>{role}님 휴대폰으로 인증번호를 보내고 있어요…</div>
                : <button type="button" onClick={() => void askCode()} style={btn(true)}>인증번호 다시 보내기</button>)   // 못 보냈을 때만(잠시 막힘 등)
            : <>
                <div style={{ fontSize: 15, color: X.label }}>{sentTo}로 문자를 보냈어요. 번호가 들어가면 바로 열려요</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={code} inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus aria-label="인증번호 6자리"
                    onChange={(e) => { const c = e.target.value.replace(/\D/g, '').slice(0, 6); setCode(c); if (c.length === 6) void verify(c); }}   // 6자리가 차면 바로(아이폰 키보드 위 번호 한 번 누르기 · 붙여 넣기)
                    onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) void verify(); }}
                    style={{ flex: 1, minWidth: 0, height: 52, borderRadius: 12, border: `1.5px solid ${X.lineDk}`, padding: '0 14px', fontSize: 20, letterSpacing: 6, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  <button type="button" disabled={busy || code.length !== 6} onClick={() => void verify()} style={{ ...btn(true), opacity: code.length === 6 ? 1 : 0.45 }}>확인</button>
                </div>
                <button type="button" disabled={busy} onClick={() => void askCode()} style={{ justifySelf: 'start', border: 'none', background: 'none', padding: '6px 0', fontFamily: 'inherit', fontSize: 15, color: X.label, textDecoration: 'underline', cursor: 'pointer' }}>인증번호 다시 받기</button>
              </>}
        </div>
      )}
      {msg && <div role="alert" style={{ marginTop: 10, fontSize: 15, fontWeight: 600, color: X.red }}>{msg}</div>}

      {open && ready && (
        <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
          {!loans && !msg && <div style={{ fontSize: 15, color: X.label }}>불러오는 중…</div>}
          {loans && loans.length === 0 && <div style={{ fontSize: 15, color: X.label }}>아직 보낸 대출이 없어요</div>}
          {loans && loans.map((l) => (
            <div key={l.id} style={{ border: `1px solid ${X.line}`, borderRadius: 12, padding: '12px 14px', display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <b style={{ flex: 1, minWidth: 0, fontSize: 18, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.company}</b>
                <span style={{ fontSize: 14, fontWeight: 700, color: l.status === '진행' ? ink : X.sub, flexShrink: 0 }}>{l.status === '진행' ? '진행 중' : l.status === '완료' ? '끝남' : '멈춤'}</span>
              </div>
              <div style={{ fontSize: 15, color: X.label }}>접수 {md(l.created)}{l.status === '진행' && l.next_check ? ` · 다음 확인 ${md(l.next_check)}` : ''}</div>
              {l.branches.map(branchLine)}
              {l.common && <div style={{ padding: '10px 12px', borderRadius: 10, background: X.soft, fontSize: 16 }}>지금 <b>{l.common.now || '대출 방안 검토'}</b> 중이에요</div>}
              {l.news && <div style={{ fontSize: 15, color: X.text }}><span style={{ color: X.label }}>최근 소식 {md(l.news.day)} · </span>{l.news.text}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
      )}
    </>
  );
}
